import { AnimationBaseController, IAnimationControllerConfiguration, Inject, SoundType } from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../../AnimationContents';
import { GameLogic } from '../../../Logic/GameLogic';
import { LaserLogic } from '../../../Logic/MiniGames/LaserLogic';
import { SpineAnimationBaseController } from '../../Spine/SpineAnimationBaseController';
import { LaserMirrorController } from './LaserMirrorController';
import { Graphics, Rectangle } from 'pixi.js';
import { LaserModel } from '../../../Models/MiniGames/LaserModel';
import { LaserDotController } from './LaserDotController';
import { SoundConstants, SoundLevels } from '../../../Constants/SoundConstants';
import { DialogTextEnum, DialogTypeTextEnum } from '../../../Models/GameModel';
import { ButtonController } from '../../../Common/ButtonController';

export interface ILaserGameControllerOptions {
    onComplete: () => void;
}

export class LaserGameController extends AnimationBaseController {
    public $model: LaserModel;

    private _width: number = 0;
    private _height: number = 0;

    private _backgroundShape: Graphics;
    private _spineAnimationController: SpineAnimationBaseController;

    public constructor(
        @Inject private _options: ILaserGameControllerOptions,
        @Inject private _laserLogic: LaserLogic,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('LaserGameController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.LASER_GAME_SCREEN_JSON,
            libraryName: 'mini_game_laser',
            sounds:[
                {
                    id: SoundConstants.LASER_SOUND,
                    type: SoundType.Music
                },
                {
                    id: SoundConstants.LASER_MIRRORING,
                    type: SoundType.SFX
                },
                {
                    id: SoundConstants.LASER_MIRROR_CATCH,
                    type: SoundType.SFX
                },
                {
                    id: SoundConstants.LASER_MIRROR_PUT,
                    type: SoundType.SFX
                },
            ],
            childControllers: {
                spineAnimation: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        key: `laser`,
                        url: `/images/spine/mini_game_laser/laser_minigame.json`,
                        animations: [
                            'start',
                            'step_1',
                            'step_2',
                            'step_3',
                        ],
                    },
                },
                mirror_1: {
                    instanceName: 'mirror_1',
                    controller: LaserMirrorController,
                    options: {
                        index: 1,
                        onSelected: index => this.onMirrorSelected(index),
                        onUnselected: index => this.onMirrorUnselected(index),
                    },
                },
                mirror_2: {
                    instanceName: 'mirror_2',
                    controller: LaserMirrorController,
                    options: {
                        index: 2,
                        onSelected: index => this.onMirrorSelected(index),
                        onUnselected: index => this.onMirrorUnselected(index),
                    },
                },
                mirror_3: {
                    instanceName: 'mirror_3',
                    controller: LaserMirrorController,
                    options: {
                        index: 3,
                        onSelected: index => this.onMirrorSelected(index),
                        onUnselected: index => this.onMirrorUnselected(index),
                    },
                },
                dot_1: {
                    instanceName: 'dot_1',
                    controller: LaserDotController,
                    options: {
                        index: 1,
                    },
                },
                dot_2: {
                    instanceName: 'dot_2',
                    controller: LaserDotController,
                    options: {
                        index: 2,
                    },
                },
                dot_3: {
                    instanceName: 'dot_3',
                    controller: LaserDotController,
                    options: {
                        index: 3,
                    },
                },
            },
        };
    }

    protected onInitialize(): void {
        this.soundManager.play(SoundConstants.LASER_SOUND, true, SoundLevels.MINIGAME_AMBIENT);
        this.display.interactive = true;

        this._width = this.display.width;
        this._height = this.display.height;

        this._spineAnimationController = this.$childControllers.get('spineAnimation');
        this._spineAnimationController.setAnimation('start', false);

        this._backgroundShape = new Graphics();
        this.display.addChildAt(this._backgroundShape, 0);

        this._gameLogic.showDialogScreen(DialogTextEnum.laser, DialogTypeTextEnum.start);
    }

    protected onResize(width: number, height: number, scale: number) {
        const scaleValue = Math.min(
            width / scale / this._width,
            height / scale / this._height,
        );

        this.scaleX = scaleValue;
        this.scaleY = scaleValue;

        if (this._backgroundShape) {
            const w = width * 10;
            const h = height * 10;

            this._backgroundShape.clear();
            this._backgroundShape.beginFill(0x3A6473);
            this._backgroundShape.drawRect(-w / 2, -h / 2, w, h);
            this._backgroundShape.endFill();
        }
    }

    protected onLandscape(): void {
    }

    protected onPortrait(): void {
    }

    private rectIntersects(a: Rectangle, b: Rectangle): boolean {
        return !(b.x > (a.x + a.width) ||
            (b.x + b.width) < a.x ||
            b.y > (a.y + a.height) ||
            (b.y + b.height) < a.y);
    };

    private onMirrorSelected(index: number): void {
        const mirrorController = this.$childControllers.get(`mirror_${index}`);

        this.soundManager.playSound(SoundConstants.LASER_MIRROR_CATCH, 0, SoundLevels.MINIGAME_ACTION);
        this.display.setChildIndex(mirrorController.display, this.display.children.length - 1);
    }

    private onMirrorUnselected(index: number): void {
        const mirrorController = this.$childControllers.get(`mirror_${index}`) as LaserMirrorController;
        const mirrorView = mirrorController.display;

        this.soundManager.playSound(SoundConstants.LASER_MIRROR_PUT, 0, SoundLevels.MINIGAME_ACTION);

        let currentDotView = null;
        let mirrorIndex = -1;

        for (let i = 1; i <= 3; i++) {
            const dotController = this.$childControllers.get(`dot_${i}`);
            const dotView = dotController.display;

            if (this.rectIntersects(mirrorView.getBounds(), dotView.getBounds())) {
                currentDotView = dotView;
                mirrorIndex = i;
                continue;
            }
        }

        if (currentDotView) {
            if (index == mirrorIndex && index == this._laserLogic.currentStep()) {
                mirrorController.toPlace(currentDotView.x, currentDotView.y);

                this._spineAnimationController.setAnimation(`step_${index}`, false);
                this.soundManager.playSound(SoundConstants.LASER_MIRRORING, 0, SoundLevels.MINIGAME_ACTION);
                this.clearResult();
            } else {
                mirrorController.toInitial();
                this._gameLogic.showDialogScreen(DialogTextEnum.laser, DialogTypeTextEnum.hint);
            }
        } else {
            mirrorController.toInitial();
        }
    }


    protected clearResult() {
        setTimeout(() => {
            if (this._laserLogic.nextStep()) {
                this._laserLogic.reset();
                this._spineAnimationController.release();
                this.soundManager.stop(SoundConstants.LASER_SOUND);

                this._options.onComplete();
                this._gameLogic.showDialogScreen(DialogTextEnum.laser, DialogTypeTextEnum.end);
            }
        }, 100);
    }

    protected onDestroy() {
        this._laserLogic.reset();
        this.soundManager.stop(SoundConstants.LASER_SOUND);
    }
}
